import { NavLink, Route, Routes, useLocation } from "react-router-dom";
import { GlassCard } from "@kj/design-system";
import {
  BarChart3,
  ClipboardList,
  Flag,
  LayoutDashboard,
  MessageSquareWarning,
  ShieldCheck,
  Users,
} from "lucide-react";
import DashboardPage from "./pages/DashboardPage";
import UsersPage from "./pages/UsersPage";
import KYCQueuePage from "./pages/KYCQueuePage";
import ReportsPage from "./pages/ReportsPage";
import MessagesModerationPage from "./pages/MessagesModerationPage";
import AnalyticsPage from "./pages/AnalyticsPage";
import LoginPage from "./pages/LoginPage";
import { useAdminAuth } from "./store/adminAuth";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/users", label: "Users", icon: Users, end: false },
  { to: "/kyc", label: "KYC Queue", icon: ShieldCheck, end: false },
  { to: "/reports", label: "Reports", icon: Flag, end: false },
  {
    to: "/moderation",
    label: "Moderation",
    icon: MessageSquareWarning,
    end: false,
  },
  { to: "/analytics", label: "Analytics", icon: BarChart3, end: false },
];

export default function App() {
  const user = useAdminAuth((s) => s.user);
  const location = useLocation();
  if (!user && location.pathname !== "/login") {
    return <LoginPage />;
  }
  if (location.pathname === "/login") {
    return <LoginPage />;
  }

  return (
    <div className="min-h-screen grid grid-cols-[240px_1fr]">
      <aside className="border-r border-border/60 bg-bg-elevated/50 backdrop-blur-md px-4 py-5 flex flex-col gap-1">
        <div className="flex items-center gap-2 px-2 mb-4">
          <div
            aria-hidden="true"
            className="h-8 w-8 rounded-xl bg-gradient-to-br from-primary-500 via-sakura-500 to-trust-500"
          />
          <div>
            <div className="font-semibold tracking-tight">SkyToLove</div>
            <div className="text-[11px] text-fg-muted uppercase tracking-widest">
              Admin
            </div>
          </div>
        </div>
        {NAV.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            className={({ isActive }) =>
              "flex items-center gap-3 rounded-xl px-3 py-2 text-sm transition-colors " +
              (isActive
                ? "bg-primary-500/15 text-primary-500"
                : "text-fg-muted hover:bg-white/5 hover:text-fg")
            }
          >
            <n.icon className="h-4 w-4" />
            {n.label}
          </NavLink>
        ))}
        <div className="mt-auto pt-4 border-t border-border/60">
          <GlassCard padding="sm" radius="lg" className="text-xs">
            <ClipboardList className="h-4 w-4 inline mr-1" />
            <b>{user?.email}</b>
            <div className="text-fg-muted">role: {user?.role}</div>
          </GlassCard>
        </div>
      </aside>

      <main className="p-8 max-w-7xl w-full mx-auto">
        <Routes>
          <Route path="/" element={<DashboardPage />} />
          <Route path="/users/*" element={<UsersPage />} />
          <Route path="/kyc/*" element={<KYCQueuePage />} />
          <Route path="/reports/*" element={<ReportsPage />} />
          <Route path="/moderation/*" element={<MessagesModerationPage />} />
          <Route path="/analytics/*" element={<AnalyticsPage />} />
        </Routes>
      </main>
    </div>
  );
}
